import React, { useEffect } from "react";
import "./tournament-round-leaderboards.scss";

import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import { faChevronLeft, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  GSActionBar,
  GSEmptyList,
  GSItemList,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  getRound,
  getRoundLeaderboards,
  selectCurrentRound,
} from "../../../../reducers/roundSlice";
import TournamentLeaderboardLineItem from "../../tournament-leaderboards/tournament-leaderboard-line-item";
import { getRoundName } from "../../../../hooks/round/roundHooks";
import {
  getTournamentLeaderboards,
  getTournamentLeaderboardSettings,
  selectLeaderboardLoading,
  selectTournamentLeaderboardSettings,
} from "../../../../reducers/leaderboardSlice";
import { usePreflighting } from "../../../../hooks/flights/flightHooks";

const TournamentRoundLeaderboards = (props) => {
  const dispatch = useDispatch();
  const match = useMatch("/:tournamentID/rounds/:roundID/leaderboards/*");
  const navigate = useNavigate();

  const tournamentID = match?.params?.tournamentID;
  const roundID = match?.params?.roundID;

  const currentRound = useSelector(selectCurrentRound);
  const leaderboardSettings = useSelector(selectTournamentLeaderboardSettings);
  const leaderboardLoading = useSelector(selectLeaderboardLoading);

  useEffect(() => {
    if (roundID) {
      dispatch(getRound(roundID));
      dispatch(getRoundLeaderboards(roundID));
    }
  }, [dispatch, roundID]);

  useEffect(() => {
    if (tournamentID) {
      dispatch(getTournamentLeaderboardSettings(tournamentID));
      dispatch(getTournamentLeaderboards(tournamentID));
    }
  }, [dispatch, tournamentID]);

  const addLeaderboard = () => {
    navigate(`/${tournamentID}/leaderboards/add`);
  };

  const viewLeaderboards = () => {
    navigate(`/${tournamentID}/leaderboards`);
  };

  const getActions = () => {
    let actions = [
      {
        name: "View Leaderboards",
        action: viewLeaderboards,
        type: "light-grey",
      },
    ];
    return actions;
  };

  const getPageAction = () => {
    return [
      {
        title: "Add Leaderboard",
        actionClick: addLeaderboard,
        actionIcon: faPlus,
        type: "black no-wrap mobile-icon",
      },
    ];
  };

  const leftNavigation = () => {
    navigate(-1);
  };

  const getNavigation = () => {
    return {
      title: `Round ${getRoundName(currentRound)} Leaderboards & Skins `,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getFilteredSettings = (isRoundLeaderboard, isSkin) => {
    if (isRoundLeaderboard || isSkin) {
      return leaderboardSettings?.filter?.(
        (ls) =>
          ls?.tournamentRound?.number === currentRound?.number &&
          ls.roundLeaderboard === isRoundLeaderboard &&
          ls.skins === isSkin
      );
    } else {
      return leaderboardSettings?.filter?.(
        (ls) =>
          ls.roundLeaderboard === isRoundLeaderboard && ls.skins === isSkin
      );
    }
  };

  const leaderboardSelected = (leaderboard) => {
    navigate(`/${tournamentID}/leaderboards/${leaderboard.id}`);
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Manage Leaderboards & Skins"
        detail="Once added, round leaderboards will be displayed here."
        actions={[
          {
            title: "Add Leaderboard",
            buttonIcon: faPlus,
            onClick: addLeaderboard,
            type: "black",
          },
        ]}
      />
    );
  };

  const getSortedLeaderboards = (leaderboards) => {
    leaderboards?.sort?.((a, b) => {
      if (b.main === false) {
        return a.name.localeCompare(b.name);
      }
      return b;
    });
  };

  const getLeaderboards = () => {
    const tournament = getFilteredSettings(false, false);
    const rounds = getFilteredSettings(true, false);
    const skins = getFilteredSettings(true, true);

    getSortedLeaderboards(tournament);
    getSortedLeaderboards(rounds);
    getSortedLeaderboards(skins);

    const indexItems = [
      {
        index: 0,
        view: <GSActionBar header="Tournament Leaderboards"></GSActionBar>,
      },
    ];

    if (rounds?.length > 0) {
      indexItems?.push?.({
        index: tournament.length,
        view: <GSActionBar header="Round Leaderboards"></GSActionBar>,
      });
    }
    if (skins.length > 0) {
      indexItems?.push?.({
        index: rounds.length + tournament.length,
        view: <GSActionBar header="Skins Leaderboards"></GSActionBar>,
      });
    }

    return (
      <GSItemList
        type="vertical selectable leaderboard-group"
        items={[...tournament, ...rounds, ...skins]}
        indexItems={indexItems}
        itemSelected={leaderboardSelected}
        emptyMessage={getEmptyListMessage()}
        loading={leaderboardLoading?.includes?.(
          "getTournamentLeaderboardSettings"
        )}
        loadingMainText="Loading Leaderboards"
        listItem={(item) => <TournamentLeaderboardLineItem {...item} />}
      />
    );
  };

  const getContent = () => {
    return (
      <gs-form>
        <GSActionBar
          type="form-header"
          header={`Round ${getRoundName(currentRound)} Leaderboards & Skins `}
          pageActions={getPageAction()}
        />
        {getLeaderboards()}
      </gs-form>
    );
  };

  const getDrawer = () => {
    return {
      actions: getActions(),
    };
  };

  const {showFlightingBanner, getBanner} = usePreflighting({tournamentID});

  return (
    <tournament-round-leaderboards>
      <GSSidePanelPage
        header={getNavigation()}
        content={getContent()}
        drawer={getDrawer()}
        banner={showFlightingBanner ? getBanner() : false}
      />
    </tournament-round-leaderboards>
  );
};

export default TournamentRoundLeaderboards;
